/* eslint-disable jsx-a11y/iframe-has-title */
import { domParser } from "../../../core/common/ults/domParser";
import { useHandleForum } from "../../../core/hooks/useHandleForum";
import { useHandleFile } from "../../../core/hooks/useHandleFile";
import Skeleton from "react-loading-skeleton";
import {
  convertTimestampToDate,
  formatTime,
} from "../../../core/common/ults/dateTime";
import { CustomEditor } from "../../../core/common/ults/editor";
import { useState } from "react";
import { Editor as TinyMCEEditor } from "tinymce";
import "./modal.css";
import { useAuth } from "../../../core/hooks/useAuth";
import { Modal, Popconfirm, Spin } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHandleQuiz } from "../../../core/hooks/useHandleQuiz";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

export const PageModal = (props: any) => {
  const { pageContent, selectedModule } = props;
  return (
    <p
      className="fs-14 mb-3"
      dangerouslySetInnerHTML={{
        __html: domParser(
          pageContent?.pages?.find(
            (p: any, idx: any) => p?.id === selectedModule?.instance
          )?.content || ""
        ),
      }}
    />
  );
};

// Hàm convert YouTube URL thành embed URL
const convertYouTubeToEmbed = (url: string) => {
  if (!url) return url;
  
  // Pattern để match các dạng YouTube URL khác nhau
  const youtubePatterns = [
    // https://www.youtube.com/watch?v=VIDEO_ID
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    // https://youtu.be/VIDEO_ID
    /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)/,
    // https://www.youtube.com/embed/VIDEO_ID (đã là embed)
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }
  }
  
  return url; // Trả về URL gốc nếu không phải YouTube
};

// Hàm kiểm tra xem có phải YouTube URL không
const isYouTubeUrl = (url: string) => {
  const urlLower = url.toLowerCase();
  return urlLower.includes('youtube.com') || urlLower.includes('youtu.be');
};

// Hàm phân loại loại file dựa trên URL hoặc extension
const getFileType = (url: string) => {
  if (!url) return 'unknown';
  
  const urlLower = url.toLowerCase();
  
  // PDF files
  if (urlLower.includes('.pdf') || urlLower.includes('pdf')) {
    return 'pdf';
  }
  
  // PowerPoint files
  if (urlLower.match(/\.(ppt|pptx|pps|ppsx)(\?|$)/) || urlLower.includes('powerpoint')) {
    return 'powerpoint';
  }
  
  // Image files
  if (urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/)) {
    return 'image';
  }
  
  // Video files (bao gồm YouTube và Vimeo)
  if (urlLower.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?|$)/) || 
      urlLower.includes('youtube') || 
      urlLower.includes('vimeo') ||
      urlLower.includes('video')) {
    return 'video';
  }
  
  // Audio files
  if (urlLower.match(/\.(mp3|wav|ogg|aac|m4a|flac|wma)(\?|$)/) ||
      urlLower.includes('audio')) {
    return 'audio';
  }
  
  // Web links
  if (urlLower.startsWith('http') || urlLower.startsWith('https')) {
    return 'web';
  }
  
  return 'unknown';
};

export const UrlModal = (props: any) => {
  const { selectedModule } = props;
  const url = selectedModule?.contents?.[0]?.fileurl;
  
  if (!url) {
    return (
      <div className="text-center py-4">
        <p className="fs-14 mb-3 text-muted">Không tìm thấy URL để hiển thị</p>
      </div>
    );
  }

  const fileType = getFileType(url);

  const renderContent = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <embed
            src={url}
            type="application/pdf"
            width="100%"
            height="600px"
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px"
            }}
          />
        );
        
      case 'powerpoint':
        return (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
            width="100%"
            height="600px"
            frameBorder="0"
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px"
            }}
          />
        );
        
      case 'image':
        return (
          <div className="text-center">
            <img
              src={url}
              alt="Content"
              className="img-fluid"
              style={{
                maxHeight: "600px",
                border: "1px solid #ddd",
                borderRadius: "8px"
              }}
            />
          </div>
        );
        
      case 'video':
        // Nếu là YouTube, sử dụng iframe
        if (isYouTubeUrl(url)) {
          const embedUrl = convertYouTubeToEmbed(url);
          return (
            <iframe
              src={embedUrl}
              width="100%"
              height="400px"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px"
              }}
            />
          );
        }
        // Nếu là video file thông thường
        return (
          <video
            controls
            width="100%"
            height="400px"
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px"
            }}
          >
            <source src={url} type="video/mp4" />
            Trình duyệt của bạn không hỗ trợ phát video.
          </video>
        );
        
      case 'audio':
        return (
          <div className="text-center py-5">
            <audio
              controls
              style={{ width: "100%" }}
            >
              <source src={url} type="audio/mpeg" />
              Trình duyệt của bạn không hỗ trợ phát audio.
            </audio>
          </div>
        );
        
      case 'web':
      default:
        return (
          <embed
            src={url}
            width="100%"
            height="600px"
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px"
            }}
          />
        );
    }
  };

  return (
    <div className="url-modal-content">
      {renderContent()}
      
      <div className="mt-3 text-center">
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-outline-primary btn-sm"
        >
          <i className="isax isax-external-link me-1" />
          Mở trong tab mới
        </a>
      </div>
    </div>
  );
};

export const ForumModal = (props: any) => {
  const { selectedModule } = props;
  const {
    forumDisscusion,
    isForumDisscusionLoading,
    createDiscussion,
    refetchDiscussions,
  } = useHandleForum(selectedModule?.instance);
  const initForumForm = {
    forumid: selectedModule?.instance.toString(),
    subject: "",
    message: "",
  };
  const [formData, setFormData] = useState(initForumForm);
  const [showCreateDiscussion, setShowCreateDiscussion] = useState(false);

  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const { t } = useTranslation();

  const handleEditorChange = (content: string, editor: TinyMCEEditor) => {
    const name = editor.id;
    setFormData((prevState) => ({
      ...prevState,
      [name]: content,
    }));
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCancleInput = () => {
    setFormData(initForumForm);
    setShowCreateDiscussion(false);
  };

  const handleSelectDiscussion = (discussion: any) => {
    setSelectedDiscussion(discussion);
    setShowDiscussionModal(true);
  };

  const handleCloseDiscussionModal = () => {
    setShowDiscussionModal(false);
    setSelectedDiscussion(null);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    createDiscussion(formData);
    setFormData(initForumForm);
    setShowCreateDiscussion(false);
  };

  return (
    <div className="forum-table-wrapper mt-4">
      <button
        className="btn btn-outline-secondary mb-4"
        onClick={() => setShowCreateDiscussion(true)}
      >
        {t("student.forums.detailTable.addNewTopic")}
      </button>
      <div className="overflow-x-auto">
        <table className="w-100 border rounded-lg text-sm">
          <thead>
            <tr className="bg-blue-700 text-white">
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.discussion")}
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.creator")}
                <span className="text-xs">↓</span>
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.latestInteraction")}
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.replies")}
              </th>
            </tr>
          </thead>
          {isForumDisscusionLoading ? (
            <tbody>
              <Skeleton height={100} className="mb-3" />
            </tbody>
          ) : (
            <>
              {forumDisscusion?.discussions?.map((item: any, idx: any) => (
                <tbody key={item?.name}>
                  <tr className="border-b">
                    <td
                      className="py-3 px-4 w-25 discussion-name cursor-pointer"
                      onClick={() => handleSelectDiscussion(item)}
                    >
                      <span className="font-medium d-flex">{item?.name}</span>
                    </td>
                    <td
                      className="py-3 px-4 w-25 cursor-pointer"
                      onClick={() => handleSelectDiscussion(item)}
                    >
                      <div className="flex items-center flex-row">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span className="block">{item?.userfullname}</span>
                          <span className="block">
                            {convertTimestampToDate(item?.created)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 w-25">
                      <div className="flex items-center flex-row">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span className="block">
                            {item?.usermodifiedfullname}
                          </span>
                          <span className="block">
                            {convertTimestampToDate(item?.timemodified)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center w-25">
                      {item?.numreplies}
                    </td>
                  </tr>
                </tbody>
              ))}
            </>
          )}
        </table>
      </div>

      {/* modal create new discussion */}
      {showCreateDiscussion && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5>{t("student.forums.detailTable.addDiscussion")}</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowCreateDiscussion(false)}
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
              <div className="modal-body">
                <form className="space-y-6" onSubmit={(e) => handleSubmit(e)}>
                  <div className="d-flex items-start gap-5 mb-3">
                    <label className="w-24 pt-2 text-sm font-medium text-gray-700 flex-shrink-0">
                      {t("student.forums.detailTable.title")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex-1">
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t("student.forums.detailTable.enterTitle")}
                        required
                        name="subject"
                        value={formData?.subject || ""}
                        onChange={handleInputChange}
                        maxLength={255}
                      />
                    </div>
                  </div>
                  <div className="d-flex w-100 items-start gap-5">
                    <label className="w-24 pt-2 text-sm font-medium text-gray-700 flex-shrink-0">
                      {t("student.forums.detailTable.content")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex-1">
                      <CustomEditor
                        value={formData?.message || ""}
                        onEditorChange={handleEditorChange}
                        id="message"
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-end gap-2 pt-4">
                    <button type="submit" className="btn btn-primary">
                      {t("student.forums.detailTable.postToForum")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleCancleInput}
                    >
                      {t("student.forums.detailTable.cancel")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}

      {/* modal view discussion detail */}
      {showDiscussionModal && (
        <DiscussionModal
          selectedDiscussion={selectedDiscussion}
          onClose={handleCloseDiscussionModal}
          forumId={selectedModule?.instance} // <- truyền thêm forumId
          refetchDiscussions={refetchDiscussions}
        />
      )}
    </div>
  );
};

const PostItem = ({
  post,
  onReply,
  onDelete,
  isReplying,
  postData,
  onCancelReply,
  onSubmit,
  onChangeInput,
  moodleUser,
}: any) => {
  const { t } = useTranslation();
  return (
    <div className="reply-item border rounded p-3 mb-3">
      <div className="d-flex align-items-start gap-3">
        <img
          src={post?.author?.urls?.profileimage || "#"}
          alt="User Avatar"
          className="rounded-circle"
          width="40"
          height="40"
        />
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-1">{post?.subject}</h6>
          </div>
          <p
            className="mb-2 text-sm text-gray-600"
            dangerouslySetInnerHTML={{
              __html: domParser(post?.html?.authorsubheading),
            }}
          />
          <div className="reply-content">
            <p
              dangerouslySetInnerHTML={{
                __html: domParser(post?.message),
              }}
            />
          </div>
          <div className="d-flex gap-2 mt-3">
            {/* <button type="button" className="btn btn-secondary btn-sm">
              {t("student.forums.detailTable.permalink")}
            </button> */}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onReply(post)}
            >
              {t("student.forums.detailTable.reply")}
            </button>
            {post?.author?.id === moodleUser?.id && (
              <Popconfirm
                title={t("student.forums.detailTable.deleteReply")}
                description={t("student.forums.detailTable.confirmDeleteReply")}
                onConfirm={() => onDelete(post)}
                okText={t("student.forums.detailTable.delete")}
                cancelText={t("student.forums.detailTable.cancel")}
              >
                <button type="button" className="btn btn-primary btn-sm">
                  {t("student.forums.detailTable.delete")}
                </button>
              </Popconfirm>
            )}
          </div>
        </div>
      </div>

      {isReplying && (
        <div className="reply-input-area mt-3 p-3 border rounded bg-light">
          <textarea
            className="form-control mb-3"
            name="message"
            placeholder={t("student.forums.detailTable.writeYourReply")}
            onChange={onChangeInput}
            value={postData?.message}
            rows={5}
          ></textarea>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onSubmit}
              >
                {t("student.forums.detailTable.postToForum")}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onCancelReply}
              >
                {t("student.forums.detailTable.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const DiscussionModal = (props: any) => {
  const { selectedDiscussion, onClose, refetchDiscussions: refetchForumDiscussions } = props;
  const {
    discussionPosts,
    isDiscussionPostsLoading,
    isDiscussionPostsFetched,
    createPost,
    deletePost,
  } = useHandleForum(selectedDiscussion?.discussion);
  const { moodleUser } = useAuth();
  const postRefactored = discussionPosts?.posts?.slice(0)?.reverse();

  // Chỉ đóng modal khi đã fetch xong và không còn posts (vd. đã xóa hết)
  // Tránh đóng sớm khi discussionPosts còn undefined lúc đang fetch lần đầu
  React.useEffect(() => {
    if (isDiscussionPostsLoading || !isDiscussionPostsFetched) return;
    if (!discussionPosts?.posts || discussionPosts.posts.length === 0) {
      onClose();
      refetchForumDiscussions?.();
    }
  }, [
    discussionPosts,
    isDiscussionPostsLoading,
    isDiscussionPostsFetched,
    onClose,
    refetchForumDiscussions,
  ]);

  // State to track which post is being replied to
  const [replyingToPostId, setReplyingToPostId] = useState<string | null>(null);

  const initPostForm = {
    postid: "",
    subject: "",
    message: "",
  };

  const [postData, setPostData] = useState(initPostForm);

  if (!selectedDiscussion) {
    return null;
  }

  const handleReplyClick = (data: any) => {
    setReplyingToPostId(data?.id);
    setPostData((prev) => ({
      ...prev,
      postid: data?.id,
      subject: data?.replysubject,
    }));
  };

  const handleCancelReply = () => {
    setReplyingToPostId(null);
    setPostData(initPostForm);
  };

  const handleChangeInput = (e: any) => {
    const { name, value } = e.target;
    setPostData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    createPost(postData);
    setPostData(initPostForm);
    setReplyingToPostId(null);
  };

  const handleDeletePost = (data: any) => {
    deletePost(data?.id, {
      onSuccess: () => {
        refetchForumDiscussions?.();
      },
    });
  };

  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5>{selectedDiscussion?.name}</h5>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <i className="isax isax-close-circle5" />
            </button>
          </div>
          <div className="modal-body">
            {isDiscussionPostsLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spin size="large" />
              </div>
            ) : (
              <div className="discussion-replies mt-4">
                {postRefactored?.map((post: any) => (
                  <PostItem
                    key={post?.id}
                    post={post}
                    onReply={handleReplyClick}
                    onDelete={handleDeletePost}
                    isReplying={replyingToPostId === post?.id}
                    postData={postData}
                    onCancelReply={handleCancelReply}
                    onSubmit={handleSubmit}
                    onChangeInput={handleChangeInput}
                    moodleUser={moodleUser}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop show"></div>
    </div>
  );
};

export const QuizModal = (props: any) => {
  const { selectedModule } = props;

  const route = all_routes;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showNotOpenModal, setShowNotOpenModal] = useState(false);

  const { moodleQuizzes, isMoodleQuizzesLoading, startQuiz } = useHandleQuiz();
  const { quizAttemptOverview } = useHandleQuiz(selectedModule?.instance);

  const lastAttempt =
    quizAttemptOverview?.attempts?.[quizAttemptOverview?.attempts?.length - 1];
  const data = moodleQuizzes?.quizzes?.find(
    (item: any) => item?.id === selectedModule?.instance
  );

  const getRoute = () => {
    if (lastAttempt?.state === "finished") {
      return `${route.studentQuizReview}/${lastAttempt?.id}`;
    }
    return `${route.studentQuizQuestion}/${lastAttempt?.id}`;
  };

  const isNotOpen = () => {
    if (!data?.timeopen || data?.timeopen === 0) return false;
    const now = Math.floor(Date.now() / 1000);
    return now < data.timeopen;
  };

  const isExpired = () => {
    if (!data?.timeclose || data?.timeclose === 0) return false;
    const now = Math.floor(Date.now() / 1000); // thời gian hiện tại (giây)
    return now > data.timeclose;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Chỉ áp dụng check cho quiz
    if (isNotOpen() && lastAttempt?.state !== "finished") {
      e.preventDefault();
      setShowNotOpenModal(true);
      return;
    }

    if (isExpired() && lastAttempt?.state !== "finished") {
      e.preventDefault();
      setShowExpiredModal(true);
      return;
    }
    if (!lastAttempt) {
      e.preventDefault();
      setShowConfirmModal(true);
    }
  };

  const handleStartQuiz = async () => {
    const response = await startQuiz(data?.id);
    setShowConfirmModal(false);
    navigate(`${route.studentQuizQuestion}/${response?.attempt?.id}`, {
      state: {
        quizName: data?.name,
        state: response?.attempt?.state,
        totalgrade: data?.grade,
      },
    });
  };

  // Dữ liệu hardcode
  return (
    <>
      {isMoodleQuizzesLoading ? (
        <Spin />
      ) : (
        <Link
          to={getRoute()}
          className="d-flex align-items-center justify-content-between forum-card hover-effect"
          onClick={handleCardClick}
          state={{
            quizName: data?.name,
            totalgrade: data?.grade,
          }}
        >
          <div>
            <p className="mb-2 item-intro">
              <span className="me-3">
                <i className="isax isax-message-edit5 me-1" />
                {t("quiz.card.type.label")}: {t(`quiz.card.type.quiz`)}
              </span>
              <span className="me-3">
                <i className="isax isax-status-up5 me-1" />
                {t("quiz.card.status.label")}:{" "}
                {!lastAttempt
                  ? t(`quiz.card.status.new`)
                  : t(`quiz.card.status.${lastAttempt?.state}`)}
              </span>
            </p>

            <p className="mb-2">
              <p className="mb-2">
                <i className="isax isax-timer-15 me-1" />
                {t("quiz.card.duration")}:{" "}
                {data?.timelimit
                  ? formatTime(data?.timelimit)
                  : "Không giới hạn thời gian"}
              </p>
              <span className="me-3">
                <i className="isax isax-timer-start5 me-1" />
                {t("quiz.card.openTime")}:{" "}
                {data?.timeopen === 0
                  ? "--"
                  : convertTimestampToDate(data?.timeopen) || "--"}
              </span>
              <span className="me-3">
                <i className="isax isax-timer-pause5 me-1" />
                {t("quiz.card.closeTime")}:{" "}
                {data?.timeclose === 0
                  ? "--"
                  : convertTimestampToDate(data?.timeclose) || "--"}
              </span>
            </p>
          </div>
        </Link>
      )}
      {/* Modal xác nhận bắt đầu quiz */}
      {showConfirmModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Xác nhận bắt đầu bài {data?.name}</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowConfirmModal(false)}
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
              <div className="modal-body">
                <p>Giới hạn thời gian</p>
                <p>
                  Lượt làm bài của bạn sẽ có giới hạn thời gian là{" "}
                  {formatTime(data?.timelimit)}. Khi bạn bắt đầu, đồng hồ đếm
                  ngược sẽ khởi động và không thể tạm dừng. Bạn phải hoàn thành
                  lượt làm trước khi thời gian hết.
                </p>
                <p>Bạn có chắc chắn muốn bắt đầu ngay bây giờ không?</p>
              </div>
              <div className="modal-footer gap-2">
                <button className="btn btn-primary" onClick={handleStartQuiz}>
                  Bắt đầu ngay
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}

      {/* Modal chưa đến giờ làm bài */}
      {showNotOpenModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Chưa đến giờ làm bài</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowNotOpenModal(false)}
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
              <div className="modal-body">
                <p>Chưa đến giờ làm bài. Bạn không thể làm bài này lúc này.</p>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}

      {/* Modal hết hạn quiz */}
      {showExpiredModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Bài tập đã quá hạn</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowExpiredModal(false)}
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
              <div className="modal-body">
                <p>Bài tập đã quá hạn nộp. Bạn không thể làm bài này nữa.</p>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}
    </>
  );
};
